import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

// ── FEATURE → PERMISSION MAP ───────────────────────────────────────────────
const FEATURE_PERMISSION_MAP: Record<string, { admin: string[]; retail: string[] }> = {
  'dashboard':             { admin:['VIEW_ADMIN_DASHBOARD'], retail:['VIEW_RETAIL_DASHBOARD','VIEW_RETAIL_DASHBOARD_BALANCE','VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS','VIEW_RETAIL_DASHBOARD_ACCOUNTS','VIEW_RETAIL_DASHBOARD_TRANSACTIONS'] },
  'transactions':          { admin:['VIEW_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSACTIONS'] },
  'account':               { admin:['VIEW_ACCOUNTS','EDIT_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_ACCOUNTS'] },
  'sepa-instant':          { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSFER','VIEW_RETAIL_PAYMENTS'] },
  'sepa-standard':         { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_TRANSFER','VIEW_RETAIL_PAYMENTS'] },
  'payees':                { admin:['VIEW_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_PAYMENTS'] },
  'batch-payments':        { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS'], retail:['VIEW_RETAIL_PAYMENTS'] },
  'cards':                 { admin:['VIEW_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_CARDS'] },
  'savings':               { admin:['VIEW_ACCOUNTS','VIEW_ADMIN_ACCOUNTS'], retail:['VIEW_RETAIL_SAVINGS'] },
  'notifications':         { admin:['VIEW_ADMIN_NOTIFICATIONS'], retail:['VIEW_RETAIL_NOTIFICATIONS'] },
  'profile':               { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_PROFILE','VIEW_RETAIL_SETTINGS'] },
  'child-wallets':         { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_CHILD_WALLETS'] },
  'crypto-wallet':         { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:['VIEW_RETAIL_CRYPTO'] },
  'stablecoin':            { admin:['VIEW_ADMIN_STABLECOIN','MANAGE_STABLECOIN_CONFIG'], retail:['VIEW_RETAIL_CRYPTO'] },
  'multisig':              { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_CRYPTO'] },
  'kyt-retail':            { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:['VIEW_RETAIL_CRYPTO'] },
  'trading-bots':          { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:['VIEW_RETAIL_TRADING_BOTS'] },
  'profit-analytics':      { admin:['VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:['VIEW_RETAIL_TRADING_BOTS'] },
  'mt5-trading':           { admin:['MANAGE_PROVIDER_CONFIG','VIEW_ADMIN_SETTINGS'], retail:['VIEW_RETAIL_MT5_TRADING'] },
  'order-book':            { admin:['MANAGE_ORDER_BOOK_PROVIDERS','VIEW_ORDER_BOOK_ANALYTICS','VIEW_ADMIN_ORDER_BOOK'], retail:['VIEW_RETAIL_ORDER_BOOK','SUBMIT_TRADING_ORDERS','CANCEL_TRADING_ORDERS','VIEW_TRADING_HISTORY'] },
  'assets':                { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','VIEW_ADMIN_ASSET_TOKENS'], retail:['VIEW_RETAIL_ASSETS'] },
  'igaming':               { admin:['VIEW_ADMIN_IGAMING'], retail:['VIEW_RETAIL_IGAMING'] },
  'p2p-games':             { admin:['VIEW_ADMIN_P2PGAME'], retail:['VIEW_RETAIL_P2PGAME'] },
  'friends':               { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:['VIEW_RETAIL_SOCIAL_MEDIA'] },
  'social-feed':           { admin:['MANAGE_SOCIAL_MEDIA_POSTS','VIEW_SOCIAL_MEDIA_ANALYTICS','VIEW_ADMIN_SOCIAL_MEDIA'], retail:['VIEW_RETAIL_SOCIAL_MEDIA'] },
  'admin-dashboard':       { admin:['VIEW_ADMIN_DASHBOARD','VIEW_DASHBOARD_TOTAL_CUSTOMERS','VIEW_DASHBOARD_ACTIVE_CUSTOMERS','VIEW_DASHBOARD_TOTAL_ACCOUNTS','VIEW_DASHBOARD_PENDING_TRANSACTIONS','VIEW_DASHBOARD_SYSTEM_HEALTH','VIEW_DASHBOARD_QUICK_INSIGHTS','VIEW_DASHBOARD_SYSTEM_STATUS'], retail:[] },
  'admin-customers':       { admin:['VIEW_CUSTOMERS','EDIT_CUSTOMERS','SUSPEND_CUSTOMERS','VIEW_ADMIN_CUSTOMERS','VIEW_KYC_DOCUMENTS','APPROVE_KYC','REJECT_KYC','VIEW_DASHBOARD_KYC_REVIEWS'], retail:[] },
  'admin-accounts':        { admin:['VIEW_ACCOUNTS','EDIT_ACCOUNTS','FREEZE_ACCOUNTS','ADJUST_BALANCES','CLOSE_ACCOUNTS','VIEW_ACCOUNT_HISTORY','VIEW_ADMIN_ACCOUNTS','VIEW_DASHBOARD_TOTAL_ACCOUNTS'], retail:[] },
  'admin-txns':            { admin:['VIEW_TRANSACTIONS','APPROVE_TRANSACTIONS','REJECT_TRANSACTIONS','REVERSE_TRANSACTIONS','VIEW_ADMIN_TRANSACTIONS','VIEW_DASHBOARD_PENDING_TRANSACTIONS'], retail:[] },
  'admin-compliance':      { admin:['VIEW_COMPLIANCE_ALERTS','MANAGE_COMPLIANCE_CASES','ACCESS_AML_TOOLS','CREATE_SAR_REPORTS','VIEW_ADMIN_COMPLIANCE','VIEW_ADMIN_KYC','VIEW_ADMIN_COMPLIANCE_BASIC','VIEW_ADMIN_COMPLIANCE_KYC_REVIEW','VIEW_DASHBOARD_COMPLIANCE_ALERTS'], retail:[] },
  'admin-reports':         { admin:['VIEW_ANALYTICS','GENERATE_REPORTS','EXPORT_DATA','VIEW_ADMIN_REPORTS','VIEW_ADMIN_ANALYTICS'], retail:[] },
  'admin-audit':           { admin:['ACCESS_AUDIT_LOGS','ACCESS_API_LOGS','VIEW_ADMIN_AUDIT'], retail:[] },
  'admin-users':           { admin:['MANAGE_ADMIN_USERS','VIEW_ADMIN_USERS'], retail:[] },
  'admin-settings':        { admin:['MANAGE_SETTINGS','CONFIGURE_SYSTEM','CONFIGURE_PRODUCTS','MANAGE_FEES','SET_INTEREST_RATES','VIEW_ADMIN_SETTINGS'], retail:[] },
  'admin-trading-bots':    { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_TRADING_STRATEGY_LOGS','CONFIGURE_TRADING_STRATEGIES','EXECUTE_TRADING_ORDERS','VIEW_TRADING_BOT_PERFORMANCE','VIEW_ADMIN_TRADING_BOTS','VIEW_DASHBOARD_TRADING_BOTS'], retail:[] },
  'admin-kucoin':          { admin:['MANAGE_TRADING_BOTS','VIEW_TRADING_BOT_ANALYTICS','VIEW_ADMIN_TRADING_BOTS'], retail:[] },
  'admin-provider':        { admin:['MANAGE_PROVIDER_CONFIG','MANAGE_INTEGRATIONS','VIEW_ADMIN_INTEGRATIONS','VIEW_ADMIN_SETTINGS'], retail:[] },
  'admin-asset-tokens':    { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','VIEW_ADMIN_ASSET_TOKENS'], retail:[] },
  'admin-stablecoin-mgmt': { admin:['VIEW_ADMIN_STABLECOIN','MANAGE_STABLECOIN_CONFIG'], retail:[] },
  'admin-order-book':      { admin:['MANAGE_ORDER_BOOK_PROVIDERS','VIEW_ORDER_BOOK_ANALYTICS','VIEW_ADMIN_ORDER_BOOK','CANCEL_ANY_ORDER','VIEW_ALL_TRADING_ORDERS'], retail:[] },
  'admin-eligibility':     { admin:['MANAGE_TRADING_BOTS','VIEW_ADMIN_TRADING_BOTS'], retail:[] },
  'admin-kyt':             { admin:['VIEW_KYT','VIEW_ADMIN_KYT'], retail:[] },
  'admin-risk':            { admin:['VIEW_ANALYTICS','VIEW_ADMIN_ANALYTICS'], retail:[] },
  'admin-health':          { admin:['CONFIGURE_SYSTEM','VIEW_ADMIN_SETTINGS','VIEW_DASHBOARD_SYSTEM_HEALTH','VIEW_DASHBOARD_SYSTEM_STATUS'], retail:[] },
  'admin-igaming':         { admin:['VIEW_ADMIN_IGAMING'], retail:[] },
  'admin-p2p':             { admin:['VIEW_ADMIN_P2PGAME'], retail:[] },
  'admin-social':          { admin:['MANAGE_SOCIAL_MEDIA_POSTS','VIEW_SOCIAL_MEDIA_ANALYTICS','SCHEDULE_SOCIAL_MEDIA_CONTENT','MODERATE_SOCIAL_MEDIA_COMMENTS','MANAGE_SOCIAL_MEDIA_CAMPAIGNS','VIEW_ADMIN_SOCIAL_MEDIA'], retail:[] },
  'admin-crypto-payouts':  { admin:['MANAGE_CRYPTO_PAYOUTS','CREATE_ADMIN_CRYPTO_WALLETS','EXECUTE_BATCH_CRYPTO_PAYOUTS','VIEW_CRYPTO_PAYOUT_HISTORY','VIEW_RETAIL_CRYPTO_WALLETS','VIEW_ADMIN_CRYPTOPAYOUTS'], retail:[] },
  'admin-provider-config': { admin:['MANAGE_INTEGRATIONS','MANAGE_PROVIDER_CONFIG','VIEW_ADMIN_INTEGRATIONS'], retail:[] },
  'admin-products':        { admin:['VIEW_ASSET_TOKENS','MANAGE_ASSET_TOKENS','CONFIGURE_PRODUCTS','VIEW_ADMIN_PRODUCTS'], retail:[] },
  'admin-review-queue':    { admin:['VIEW_COMPLIANCE_ALERTS','MANAGE_COMPLIANCE_CASES','VIEW_ADMIN_COMPLIANCE'], retail:[] },
  'admin-multisig':        { admin:['VIEW_CUSTOMERS','VIEW_ADMIN_CUSTOMERS'], retail:[] },
};

const BASE_ADMIN_PERMISSIONS = [
  'VIEW_ADMIN_DASHBOARD', 'VIEW_ADMIN_NOTIFICATIONS',
  'VIEW_DASHBOARD_SYSTEM_HEALTH', 'VIEW_DASHBOARD_QUICK_INSIGHTS', 'VIEW_DASHBOARD_SYSTEM_STATUS',
];
const BASE_RETAIL_PERMISSIONS = [
  'VIEW_RETAIL_DASHBOARD', 'VIEW_RETAIL_DASHBOARD_BALANCE',
  'VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS', 'VIEW_RETAIL_DASHBOARD_ACCOUNTS',
  'VIEW_RETAIL_DASHBOARD_TRANSACTIONS', 'VIEW_RETAIL_NOTIFICATIONS',
  'VIEW_RETAIL_PROFILE', 'VIEW_RETAIL_SETTINGS',
];

function derivePermissions(featureIds: string[]) {
  const adminSet = new Set(BASE_ADMIN_PERMISSIONS);
  const retailSet = new Set(BASE_RETAIL_PERMISSIONS);
  featureIds.forEach(id => {
    const map = FEATURE_PERMISSION_MAP[id];
    if (map) {
      map.admin.forEach(p => adminSet.add(p));
      map.retail.forEach(p => retailSet.add(p));
    }
  });
  return { adminPerms: Array.from(adminSet), retailPerms: Array.from(retailSet) };
}

// ── FEATURE DISPLAY NAMES ─────────────────────────────────────────────────
const FEATURE_NAMES: Record<string, string> = {
  'dashboard':'Dashboard', 'transactions':'Transactions', 'account':'Account Overview',
  'sepa-instant':'SEPA Instant', 'sepa-standard':'SEPA Standard', 'payees':'Payees',
  'batch-payments':'Batch Payments', 'cards':'Cards', 'savings':'Savings',
  'notifications':'Notifications', 'profile':'Profile & Settings', 'child-wallets':'Child Wallets',
  'crypto-wallet':'Crypto Wallet', 'stablecoin':'Stablecoin Payments', 'multisig':'Multi-Sig Wallets',
  'kyt-retail':'KYT Risk Check', 'trading-bots':'Trading Bots', 'profit-analytics':'Profit Analytics',
  'mt5-trading':'MT5 Trading', 'order-book':'Order Book', 'assets':'Asset Tokens',
  'igaming':'iGaming', 'p2p-games':'P2P Games', 'friends':'Friends', 'social-feed':'Social Feed',
  'admin-dashboard':'Admin Dashboard', 'admin-customers':'Customer Mgmt', 'admin-accounts':'Account Mgmt',
  'admin-txns':'Transaction Mgmt', 'admin-compliance':'Compliance & KYC', 'admin-reports':'Reports',
  'admin-audit':'Audit Logs', 'admin-users':'Admin Users', 'admin-settings':'Admin Settings',
  'admin-trading-bots':'Trading Bots Admin', 'admin-kucoin':'KuCoin Integration',
  'admin-provider':'Provider Config', 'admin-asset-tokens':'Asset Token Admin',
  'admin-stablecoin-mgmt':'Stablecoin Mgmt', 'admin-order-book':'Order Book Admin',
  'admin-eligibility':'Eligibility Mgmt', 'admin-kyt':'KYT Analysis', 'admin-risk':'Risk Mgmt',
  'admin-health':'System Health', 'admin-igaming':'iGaming Admin', 'admin-p2p':'P2P Admin',
  'admin-social':'Social Media Admin', 'admin-crypto-payouts':'Crypto Payouts',
  'admin-provider-config':'Provider Integrations', 'admin-products':'Products',
  'admin-review-queue':'Review Queue', 'admin-multisig':'Multi-Sig Admin',
};

// ── MANUAL SETUP CHECKLIST ────────────────────────────────────────────────
const FEATURE_CHECKLIST: Record<string, string[]> = {
  'trading-bots':          ['Create at least one trading strategy → Admin: Trading Bots → Strategy Manager', 'Configure KuCoin API keys → Admin: Trading Provider'],
  'admin-trading-bots':    ['Create trading strategy templates in Trading Bots panel', 'Set bot eligibility rules → Eligibility Management'],
  'admin-kucoin':          ['Add KuCoin API Key & Secret → Trading Provider → KuCoin tab'],
  'assets':                ['Create asset tokens → Admin: Asset Tokens → Add Token', 'Publish tokens via Products panel'],
  'admin-asset-tokens':    ['Add at least one asset token in Asset Tokens panel', 'Set token pricing and availability in Products panel'],
  'stablecoin':            ['Enable desired stablecoins → Admin: Stablecoin Management → toggle enabled', 'Configure transaction limits per stablecoin/network'],
  'admin-stablecoin-mgmt': ['Review and enable stablecoins → Stablecoin Management', 'Set per-coin tx, daily, and minimum limits'],
  'mt5-trading':           ['Configure MT5 provider credentials → Admin: Trading Provider', 'Create a test MT5 trading account → My Trading Accounts'],
  'admin-provider':        ['Add provider credentials → Admin: Provider Config', 'Test provider connectivity from the Provider Config panel'],
  'igaming':               ['Enable iGaming module → Admin: Settings', 'Configure iGaming provider credentials → Admin: iGaming Management'],
  'admin-igaming':         ['Set up iGaming provider → Admin: iGaming Management', 'Create initial game categories'],
  'admin-compliance':      ['Configure AML alert thresholds → Admin: Compliance', 'Set KYC document requirements and review workflow'],
  'admin-kyt':             ['Configure KYT risk thresholds → Admin: KYT Analysis', 'Add or update sanctions screening lists'],
  'crypto-wallet':         ['⚠️ NOTE: blockchain.enabled=false — crypto addresses are mocked. Enable in application.properties for real blockchain.'],
  'order-book':            ['Configure order book provider → Admin: Order Book Management', 'Set trading pairs, tick sizes, and fee tiers'],
  'admin-order-book':      ['Add liquidity providers → Admin: Order Book Management', 'Configure matching engine rules → Matching Engine'],
  'social-feed':           ['Configure social media settings → Admin: Social Media', 'Set content moderation policies'],
  'admin-social':          ['Set up social content publishing rules → Admin: Social Media Management'],
  'admin-settings':        ['Configure system-wide settings → Admin: Settings (fees, limits, email templates)', 'Enable/disable feature flags per environment'],
  'admin-reports':         ['Configure report export formats and schedules → Admin: Reports'],
  'p2p-games':             ['Configure P2P game rules and prize structures → Admin: P2P Games'],
  'admin-crypto-payouts':  ['Set up admin crypto wallet → Admin: Crypto Payouts', 'Configure payout approval thresholds'],
};

// ── HELPERS ───────────────────────────────────────────────────────────────
function generatePassword(): string {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const nums  = '0123456789';
  const syms  = '!@#$%^&*';
  const all   = lower + upper + nums + syms;
  let p = lower[Math.floor(Math.random()*lower.length)]
        + upper[Math.floor(Math.random()*upper.length)]
        + nums [Math.floor(Math.random()*nums.length)]
        + syms [Math.floor(Math.random()*syms.length)];
  for (let i = 0; i < 8; i++) p += all[Math.floor(Math.random()*all.length)];
  return p.split('').sort(() => Math.random() - 0.5).join('');
}

// Must match exactly the values in com.bankingapp.entity.AdminDepartment
const DEPARTMENTS = [
  'OPERATIONS',
  'COMPLIANCE',
  'RISK_MANAGEMENT',
  'CUSTOMER_SUPPORT',
  'FINANCE',
  'TREASURY',
  'IT',
  'IT_SECURITY',
  'AUDIT',
  'PRODUCT_MANAGEMENT',
  'GOVERNANCE_KYT',
  'CRYPTO',
];
// Must match exactly the values in com.bankingapp.entity.AdminRole
const ROLES = [
  'SUPER_ADMIN',
  'OPERATIONS_MANAGER',
  'COMPLIANCE_OFFICER',
  'CUSTOMER_SUPPORT',
  'FINANCIAL_ANALYST',
  'RISK_MANAGER',
  'IT_ADMIN',
  'AUDITOR',
  'TREASURY_MANAGER',
  'VIEWER',
  'CRYPTO_GOVERNANCE_KYT',
  'CRYPTO',
];

const STEP_LABELS = ['Review Features', 'Create Admin Account', 'Create Retail User', 'Configuration Checklist', 'Complete'];

// ── CHECKLIST SUB-COMPONENT ───────────────────────────────────────────────
const Checklist: React.FC<{ items: string[] }> = ({ items }) => {
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const toggle = (i: number) => setChecked(prev => {
    const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n;
  });
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} onClick={() => toggle(i)}
          style={{display:'flex',alignItems:'flex-start',gap:12,padding:'10px 12px',borderRadius:8,
            cursor:'pointer',marginBottom:8,transition:'all 0.2s',
            background:checked.has(i)?'#f0fdf4':'#f9fafb',
            border:`1px solid ${checked.has(i)?'#bbf7d0':'#e5e7eb'}`}}>
          <div style={{width:20,height:20,borderRadius:4,flexShrink:0,marginTop:2,
            border:`2px solid ${checked.has(i)?'#16a34a':'#d1d5db'}`,
            background:checked.has(i)?'#16a34a':'#fff',
            display:'flex',alignItems:'center',justifyContent:'center'}}>
            {checked.has(i) && <span style={{color:'#fff',fontSize:12,lineHeight:1}}>✓</span>}
          </div>
          <span style={{fontSize:13,lineHeight:1.55,
            color:checked.has(i)?'#15803d':'#374151',
            textDecoration:checked.has(i)?'line-through':'none'}}>
            {item}
          </span>
        </div>
      ))}
      <div style={{fontSize:12,color:'#9ca3af',marginTop:4,textAlign:'right'}}>
        {checked.size}/{items.length} completed
      </div>
    </div>
  );
};

// ── STAT CARD ─────────────────────────────────────────────────────────────
const StatCard: React.FC<{value:number;label:string;color:string;bg:string;border:string}> = ({value,label,color,bg,border}) => (
  <div style={{background:bg,border:`1px solid ${border}`,borderRadius:12,padding:20,textAlign:'center'}}>
    <div style={{fontSize:30,fontWeight:800,color}}>{value}</div>
    <div style={{fontSize:12,color:'#6b7280',marginTop:4,lineHeight:1.3}}>{label}</div>
  </div>
);

// ── PERMISSION BADGE LIST ─────────────────────────────────────────────────
const PermBadgeList: React.FC<{perms:string[];onRemove?:(p:string)=>void;color:string;bg:string}> = ({perms,onRemove,color,bg}) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:6,maxHeight:150,overflowY:'auto',paddingBottom:4}}>
    {perms.map(p => (
      <span key={p} style={{background:bg,color,padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
        {p}
        {onRemove && (
          <button onClick={() => onRemove(p)}
            style={{background:'none',border:'none',color,cursor:'pointer',fontSize:13,padding:0,lineHeight:1,marginLeft:2}}>×</button>
        )}
      </span>
    ))}
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────
const ClientOnboarding: React.FC = () => {
  const [step, setStep]           = useState(1);
  const [features, setFeatures]   = useState<string[]>([]);
  const [adminPerms, setAdminPerms] = useState<string[]>([]);
  const [retailPerms, setRetailPerms] = useState<string[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // Step 2 – Admin account form
  const [adminForm, setAdminForm] = useState({
    username:'', email:'', firstName:'', lastName:'',
    role:'VIEWER', department:'IT', password:'', confirmPassword:''
  });
  const [adminPermOverrides, setAdminPermOverrides] = useState<string[]>([]);
  const [createdAdmin, setCreatedAdmin] = useState<Record<string,any> | null>(null);

  // Step 3 – Retail user form
  const [retailForm, setRetailForm] = useState({
    username:'', email:'', firstName:'', lastName:'',
    phone:'', userType:'RETAIL', riskCategory:'NORMAL', password:''
  });
  const [skipRetail, setSkipRetail]     = useState(false);
  const [createdRetail, setCreatedRetail] = useState<Record<string,any> | null>(null);

  // ── Parse URL params on mount ─────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let featureIds: string[] = [];

    // Support base64-encoded data packet (from CLIENT_FEATURE_SELECTOR)
    const dataParam = params.get('data');
    if (dataParam) {
      try {
        const decoded = JSON.parse(atob(decodeURIComponent(dataParam)));
        featureIds = decoded.features || [];
      } catch { /* ignore malformed data */ }
    } else {
      // Fallback: plain comma-separated features param
      const featParam = params.get('features');
      if (featParam) featureIds = featParam.split(',').filter(Boolean);
    }

    if (featureIds.length > 0) {
      setFeatures(featureIds);
      const { adminPerms: ap, retailPerms: rp } = derivePermissions(featureIds);
      setAdminPerms(ap);
      setAdminPermOverrides(ap);
      setRetailPerms(rp);
    }

    // Pre-generate passwords
    setAdminForm(prev => ({ ...prev, password: generatePassword(), confirmPassword: '' }));
    setRetailForm(prev => ({ ...prev, password: generatePassword() }));
  }, []);

  // ── Checklist items from selected features ────────────────────────────
  const checklistItems = useMemo(() => {
    const items: string[] = [];
    features.forEach(fid => {
      const checks = FEATURE_CHECKLIST[fid];
      if (checks) items.push(...checks);
    });
    return Array.from(new Set(items));
  }, [features]);

  // ── Step 2: Create admin user ─────────────────────────────────────────
  const handleCreateAdmin = useCallback(async () => {
    if (!adminForm.username.trim() || !adminForm.email.trim() ||
        !adminForm.firstName.trim() || !adminForm.lastName.trim() ||
        !adminForm.password || !adminForm.department) {
      setError('Please fill in all required fields'); return;
    }
    if (adminForm.password !== adminForm.confirmPassword) {
      setError('Passwords do not match'); return;
    }
    if (adminForm.password.length < 8) {
      setError('Password must be at least 8 characters'); return;
    }
    setError(null); setLoading(true);
    try {
      const userData = {
        username: adminForm.username, email: adminForm.email,
        firstName: adminForm.firstName, lastName: adminForm.lastName,
        role: adminForm.role, department: adminForm.department,
        permissions: adminPermOverrides, password: adminForm.password,
      };
      const response = await adminApiService.createAdminUser(userData);
      if (response.success) {
        setCreatedAdmin({ ...userData, id: (response.data as any)?.id });
        setStep(3);
      } else {
        setError(response.error || 'Failed to create admin user');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create admin user');
    } finally { setLoading(false); }
  }, [adminForm, adminPermOverrides]);

  // ── Step 3: Create retail user ────────────────────────────────────────
  const handleCreateRetail = useCallback(async () => {
    if (skipRetail) { setStep(4); return; }
    if (!retailForm.username.trim() || !retailForm.email.trim() ||
        !retailForm.firstName.trim() || !retailForm.lastName.trim() || !retailForm.password) {
      setError('Please fill in all required fields'); return;
    }
    if (retailForm.password.length < 8) {
      setError('Password must be at least 8 characters'); return;
    }
    setError(null); setLoading(true);
    try {
      // Step 3a: Create the retail user account
      const response = await adminApiService.createRetailUser(retailForm);
      if (!response.success) {
        setError(response.error || 'Failed to create retail user');
        return;
      }

      const newUserId = (response.data as any)?.id || (response.data as any)?.data?.id;

      // Step 3b: Apply derived retail permissions (if any features were selected and user was created with an ID)
      if (newUserId && retailPerms.length > 0) {
        const permResponse = await adminApiService.updateRetailUserPermissions(newUserId, retailPerms);
        if (!permResponse.success) {
          // Non-fatal: user is created but permissions failed — warn rather than block
          console.warn('Retail user created but permissions could not be applied:', permResponse.error);
          setCreatedRetail({
            ...retailForm, id: newUserId,
            permissionsApplied: false,
            permissionsError: permResponse.error || 'Permissions not applied',
          });
        } else {
          setCreatedRetail({
            ...retailForm, id: newUserId,
            permissionsApplied: true,
            appliedPermissions: retailPerms,
          });
        }
      } else {
        setCreatedRetail({ ...retailForm, id: newUserId, permissionsApplied: false });
      }

      setStep(4);
    } catch (e: any) {
      setError(e.message || 'Failed to create retail user');
    } finally { setLoading(false); }
  }, [retailForm, skipRetail, retailPerms]);

  const goToTab = (tab: string) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('tab', tab);
    window.location.href = newUrl.toString();
  };

  // ─────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6" style={{maxWidth:900,margin:'0 auto'}}>

      {/* ── Header ── */}
      <div style={{marginBottom:28}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:6}}>
          <span style={{fontSize:30}}>🚀</span>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Client Onboarding Wizard</h1>
            <p style={{fontSize:13,color:'#6b7280',margin:'4px 0 0'}}>
              {features.length > 0
                ? `Setting up ${features.length} selected features — derived ${adminPerms.length} admin + ${retailPerms.length} retail permissions`
                : 'Create client accounts and configure the platform. Open from Client Feature Selector to pre-load features.'}
            </p>
          </div>
        </div>

        {/* Step progress bar */}
        <div style={{display:'flex',alignItems:'center',marginTop:20,overflowX:'auto',paddingBottom:4}}>
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,minWidth:80}}>
                <div style={{
                  width:34,height:34,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
                  fontWeight:700,fontSize:13,transition:'all 0.25s',
                  background: step === i+1 ? '#086afb' : step > i+1 ? '#0dca9d' : '#e5e7eb',
                  color: step >= i+1 ? '#fff' : '#9ca3af',
                  boxShadow: step === i+1 ? '0 0 0 4px rgba(8,106,251,0.15)' : 'none'
                }}>
                  {step > i+1 ? '✓' : i+1}
                </div>
                <span style={{fontSize:10,fontWeight:600,textAlign:'center',maxWidth:72,lineHeight:1.3,
                  color: step === i+1 ? '#086afb' : step > i+1 ? '#0dca9d' : '#9ca3af'}}>
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div style={{flex:1,height:2,minWidth:16,transition:'all 0.25s',
                  background: step > i+1 ? '#0dca9d' : '#e5e7eb'}} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={{background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:8,padding:'12px 16px',
          marginBottom:20,color:'#dc2626',fontSize:13,display:'flex',alignItems:'center',gap:8}}>
          <span>⚠️</span> <span style={{flex:1}}>{error}</span>
          <button onClick={() => setError(null)}
            style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STEP 1 — FEATURE REVIEW
      ══════════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14,marginBottom:24}}>
            <StatCard value={features.length}      label="Features Selected"         color="#086afb" bg="#eff6ff" border="#bfdbfe" />
            <StatCard value={adminPerms.length}    label="Admin Permissions Required" color="#0dca9d" bg="#f0fdf4" border="#bbf7d0" />
            <StatCard value={retailPerms.length}   label="Retail Permissions Required" color="#e7038e" bg="#fdf2f8" border="#f9a8d4" />
            <StatCard value={checklistItems.length} label="Manual Setup Steps"        color="#f59e0b" bg="#fffbeb" border="#fde68a" />
          </div>

          {features.length === 0 && (
            <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:8,padding:14,marginBottom:20,fontSize:13,color:'#78350f'}}>
              ℹ️ <strong>No features loaded from URL.</strong> You can still create accounts manually, or open this wizard from the&nbsp;
              <em>Client Feature Selector</em> tool using the <strong>🚀 Admin Setup</strong> button.
            </div>
          )}

          {features.length > 0 && (
            <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:20,marginBottom:18}}>
              <h3 style={{fontSize:14,fontWeight:700,marginBottom:10,color:'#374151'}}>📋 Selected Features ({features.length})</h3>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {features.map(fid => (
                  <span key={fid} style={{background:'#eff6ff',color:'#1d4ed8',padding:'4px 12px',
                    borderRadius:20,fontSize:12,fontWeight:600}}>
                    {FEATURE_NAMES[fid] || fid}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
            <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:18}}>
              <h3 style={{fontSize:13,fontWeight:700,marginBottom:10,color:'#0dca9d'}}>
                ⚙️ Admin Permissions ({adminPerms.length})
              </h3>
              {adminPerms.length > 0
                ? <PermBadgeList perms={adminPerms} color="#15803d" bg="#dcfce7" />
                : <p style={{fontSize:12,color:'#9ca3af'}}>No features selected yet.</p>}
            </div>
            <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:18}}>
              <h3 style={{fontSize:13,fontWeight:700,marginBottom:10,color:'#e7038e'}}>
                👤 Retail Permissions ({retailPerms.length})
              </h3>
              {retailPerms.length > 0
                ? <PermBadgeList perms={retailPerms} color="#9d174d" bg="#fce7f3" />
                : <p style={{fontSize:12,color:'#9ca3af'}}>No features selected yet.</p>}
            </div>
          </div>

          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button className="admin-btn-primary" onClick={() => setStep(2)}
              style={{padding:'11px 30px',fontSize:14}}>
              Continue → Create Admin Account
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STEP 2 — CREATE ADMIN ACCOUNT
      ══════════════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div>
          <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:24,marginBottom:20}}>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:4,color:'#111827'}}>👤 Create Admin Account</h3>
            <p style={{fontSize:13,color:'#6b7280',marginBottom:20}}>
              This account will be granted the admin permissions derived from the selected features.
              You can remove individual permissions below before creating.
            </p>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div>
                <label className="admin-label">First Name *</label>
                <input className="admin-input" value={adminForm.firstName}
                  onChange={e => setAdminForm(p => ({...p, firstName: e.target.value}))} placeholder="John" />
              </div>
              <div>
                <label className="admin-label">Last Name *</label>
                <input className="admin-input" value={adminForm.lastName}
                  onChange={e => setAdminForm(p => ({...p, lastName: e.target.value}))} placeholder="Smith" />
              </div>
              <div>
                <label className="admin-label">Username *</label>
                <input className="admin-input" value={adminForm.username}
                  onChange={e => setAdminForm(p => ({...p, username: e.target.value}))} placeholder="admin.john" />
              </div>
              <div>
                <label className="admin-label">Email *</label>
                <input className="admin-input" type="email" value={adminForm.email}
                  onChange={e => setAdminForm(p => ({...p, email: e.target.value}))} placeholder="john@company.com" />
              </div>
              <div>
                <label className="admin-label">Role *</label>
                <select className="admin-input" value={adminForm.role}
                  onChange={e => setAdminForm(p => ({...p, role: e.target.value}))}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="admin-label">Department *</label>
                <select className="admin-input" value={adminForm.department}
                  onChange={e => setAdminForm(p => ({...p, department: e.target.value}))}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="admin-label">Password *</label>
                <div style={{display:'flex',gap:8}}>
                  <input className="admin-input" type="text" value={adminForm.password} style={{flex:1,fontFamily:'monospace'}}
                    onChange={e => setAdminForm(p => ({...p, password: e.target.value}))} />
                  <button type="button"
                    onClick={() => { const pwd=generatePassword(); setAdminForm(p=>({...p,password:pwd,confirmPassword:pwd})); }}
                    style={{padding:'8px 12px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>
                    🔀 Generate
                  </button>
                </div>
              </div>
              <div>
                <label className="admin-label">Confirm Password *</label>
                <input className="admin-input" type="text" value={adminForm.confirmPassword} style={{fontFamily:'monospace'}}
                  onChange={e => setAdminForm(p => ({...p, confirmPassword: e.target.value}))} />
              </div>
            </div>

            {/* Permissions preview with remove buttons */}
            <div style={{marginTop:20,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:16}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <h4 style={{fontSize:13,fontWeight:700,color:'#15803d',margin:0}}>
                  ⚙️ Admin Permissions to Grant ({adminPermOverrides.length})
                </h4>
                <span style={{fontSize:11,color:'#6b7280'}}>Click × to remove a permission</span>
              </div>
              <PermBadgeList
                perms={adminPermOverrides}
                onRemove={p => setAdminPermOverrides(prev => prev.filter(x => x !== p))}
                color="#15803d" bg="#dcfce7"
              />
              {adminPermOverrides.length === 0 && (
                <p style={{fontSize:12,color:'#9ca3af',margin:0}}>
                  No permissions selected — the user will only have base access.
                </p>
              )}
            </div>
          </div>

          <div style={{display:'flex',justifyContent:'space-between'}}>
            <button onClick={() => { setStep(1); setError(null); }}
              style={{padding:'10px 24px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>
              ← Back
            </button>
            <button className="admin-btn-primary" onClick={handleCreateAdmin}
              style={{padding:'11px 28px',fontSize:14}} disabled={loading}>
              {loading ? '⏳ Creating…' : '✅ Create Admin Account →'}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STEP 3 — CREATE RETAIL TEST USER
      ══════════════════════════════════════════════════════════════ */}
      {step === 3 && (
        <div>
          <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:24,marginBottom:20}}>
            {createdAdmin && (
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:12,marginBottom:18,fontSize:13}}>
                ✅ Admin account <strong>{createdAdmin.username}</strong> ({createdAdmin.role}) created successfully!
              </div>
            )}

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <h3 style={{fontSize:16,fontWeight:700,color:'#111827',margin:0}}>👤 Create Test Retail User</h3>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',userSelect:'none'}}>
                <input type="checkbox" checked={skipRetail}
                  onChange={e => { setSkipRetail(e.target.checked); setError(null); }} />
                Skip this step
              </label>
            </div>
            <p style={{fontSize:13,color:'#6b7280',marginBottom:20}}>
              Create a test retail user to verify the platform works with the selected feature set.
            </p>

            {!skipRetail ? (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <div>
                  <label className="admin-label">First Name *</label>
                  <input className="admin-input" value={retailForm.firstName}
                    onChange={e => setRetailForm(p => ({...p, firstName: e.target.value}))} placeholder="Jane" />
                </div>
                <div>
                  <label className="admin-label">Last Name *</label>
                  <input className="admin-input" value={retailForm.lastName}
                    onChange={e => setRetailForm(p => ({...p, lastName: e.target.value}))} placeholder="Doe" />
                </div>
                <div>
                  <label className="admin-label">Username *</label>
                  <input className="admin-input" value={retailForm.username}
                    onChange={e => setRetailForm(p => ({...p, username: e.target.value}))} placeholder="jane.doe" />
                </div>
                <div>
                  <label className="admin-label">Email *</label>
                  <input className="admin-input" type="email" value={retailForm.email}
                    onChange={e => setRetailForm(p => ({...p, email: e.target.value}))} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="admin-label">Phone</label>
                  <input className="admin-input" value={retailForm.phone}
                    onChange={e => setRetailForm(p => ({...p, phone: e.target.value}))} placeholder="+44 7700 900000" />
                </div>
                <div>
                  <label className="admin-label">Risk Category</label>
                  <select className="admin-input" value={retailForm.riskCategory}
                    onChange={e => setRetailForm(p => ({...p, riskCategory: e.target.value}))}>
                    <option value="NORMAL">NORMAL</option>
                    <option value="LOW">LOW</option>
                    <option value="HIGH">HIGH</option>
                  </select>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label className="admin-label">Password *</label>
                  <div style={{display:'flex',gap:8}}>
                    <input className="admin-input" type="text" value={retailForm.password} style={{flex:1,fontFamily:'monospace'}}
                      onChange={e => setRetailForm(p => ({...p, password: e.target.value}))} />
                    <button type="button"
                      onClick={() => setRetailForm(p => ({...p, password: generatePassword()}))}
                      style={{padding:'8px 12px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontSize:11}}>
                      🔀 Generate
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:8,padding:14,fontSize:13,color:'#78350f'}}>
                ℹ️ Skipping retail user creation. You can create retail users later from&nbsp;
                <strong>Admin Users → Retail Users</strong> tab.
              </div>
            )}

            {retailPerms.length > 0 && (
              <div style={{marginTop:16,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:12,fontSize:12}}>
                <div style={{fontWeight:700,color:'#15803d',marginBottom:8}}>
                  🔑 {retailPerms.length} Retail Permission{retailPerms.length !== 1 ? 's' : ''} will be automatically applied
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                  {retailPerms.map(p => (
                    <span key={p} style={{background:'#dcfce7',color:'#15803d',borderRadius:4,padding:'2px 7px',fontSize:11,fontFamily:'monospace'}}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{display:'flex',justifyContent:'space-between'}}>
            <button onClick={() => { setStep(2); setError(null); }}
              style={{padding:'10px 24px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>
              ← Back
            </button>
            <button className="admin-btn-primary" onClick={handleCreateRetail}
              style={{padding:'11px 28px',fontSize:14}} disabled={loading}>
              {loading ? '⏳ Creating…' : skipRetail ? 'Skip → Checklist' : '✅ Create Retail User →'}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STEP 4 — CONFIGURATION CHECKLIST
      ══════════════════════════════════════════════════════════════ */}
      {step === 4 && (
        <div>
          <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:24,marginBottom:20}}>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:4,color:'#111827'}}>✅ Manual Configuration Checklist</h3>
            <p style={{fontSize:13,color:'#6b7280',marginBottom:20}}>
              Complete these steps in the admin panel to fully activate the selected features. Tick each item as you go.
            </p>

            {checklistItems.length === 0 ? (
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:16,fontSize:13,color:'#15803d'}}>
                🎉 No additional manual configuration required for the selected feature set!
              </div>
            ) : (
              <Checklist items={checklistItems} />
            )}
          </div>

          <div style={{display:'flex',justifyContent:'space-between'}}>
            <button onClick={() => { setStep(3); setError(null); }}
              style={{padding:'10px 24px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>
              ← Back
            </button>
            <button className="admin-btn-primary" onClick={() => setStep(5)}
              style={{padding:'11px 28px',fontSize:14}}>
              Complete Setup →
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STEP 5 — COMPLETE
      ══════════════════════════════════════════════════════════════ */}
      {step === 5 && (
        <div>
          <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:12,padding:28,textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:68,marginBottom:14}}>🎉</div>
            <h2 style={{fontSize:22,fontWeight:800,color:'#111827',marginBottom:8}}>Setup Complete!</h2>
            <p style={{fontSize:14,color:'#6b7280',marginBottom:28}}>
              {features.length > 0
                ? `Client environment configured with ${features.length} features.`
                : 'Accounts created successfully.'}
            </p>

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:16,textAlign:'left',marginBottom:24}}>
              {createdAdmin && (
                <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:18}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#15803d',marginBottom:10,letterSpacing:'0.05em'}}>
                    ✅ ADMIN ACCOUNT CREATED
                  </div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Username:</strong> {createdAdmin.username}</div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Email:</strong> {createdAdmin.email}</div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Role:</strong> {createdAdmin.role}</div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Department:</strong> {createdAdmin.department}</div>
                  <div style={{fontSize:13,marginBottom:10}}><strong>Permissions:</strong> {adminPermOverrides.length}</div>
                  <div style={{fontSize:11,background:'#fee2e2',color:'#dc2626',borderRadius:6,padding:'6px 10px'}}>
                    ⚠️ Store the password securely — it will not be shown again.
                  </div>
                </div>
              )}
              {createdRetail && (
                <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:12,padding:18}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#1d4ed8',marginBottom:10,letterSpacing:'0.05em'}}>
                    ✅ RETAIL TEST USER CREATED
                  </div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Username:</strong> {createdRetail.username}</div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Email:</strong> {createdRetail.email}</div>
                  <div style={{fontSize:13,marginBottom:5}}><strong>Type:</strong> {createdRetail.userType}</div>
                  <div style={{fontSize:13,marginBottom:8}}><strong>Risk:</strong> {createdRetail.riskCategory}</div>
                  {/* Permissions status */}
                  {createdRetail.permissionsApplied === true ? (
                    <div style={{fontSize:11,background:'#f0fdf4',border:'1px solid #bbf7d0',color:'#15803d',borderRadius:6,padding:'6px 10px',marginBottom:8}}>
                      🔑 {(createdRetail.appliedPermissions as string[])?.length || 0} retail permissions applied automatically
                    </div>
                  ) : createdRetail.permissionsError ? (
                    <div style={{fontSize:11,background:'#fff7ed',border:'1px solid #fed7aa',color:'#c2410c',borderRadius:6,padding:'6px 10px',marginBottom:8}}>
                      ⚠️ User created but permissions could not be applied: {createdRetail.permissionsError}
                      <br/>You can apply them manually from <strong>Admin Users → Retail Users</strong>.
                    </div>
                  ) : retailPerms.length === 0 ? (
                    <div style={{fontSize:11,background:'#f3f4f6',color:'#6b7280',borderRadius:6,padding:'6px 10px',marginBottom:8}}>
                      ℹ️ No feature-specific permissions were derived — user has default retail access.
                    </div>
                  ) : null}
                  <div style={{fontSize:11,background:'#fee2e2',color:'#dc2626',borderRadius:6,padding:'6px 10px'}}>
                    ⚠️ Store the password securely — it will not be shown again.
                  </div>
                </div>
              )}
              {!createdAdmin && !createdRetail && (
                <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:12,padding:18,gridColumn:'1/-1',textAlign:'center'}}>
                  <p style={{fontSize:13,color:'#78350f',margin:0}}>
                    No accounts were created in this session. You may have skipped both creation steps.
                  </p>
                </div>
              )}
            </div>

            {checklistItems.length > 0 && (
              <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:8,padding:12,fontSize:13,textAlign:'left',marginBottom:20}}>
                ⚠️ <strong>{checklistItems.length} manual setup step{checklistItems.length>1?'s':''}</strong> may still be pending.
                Return to the <strong>Configuration Checklist</strong> tab above to complete them, or navigate to the relevant admin panel sections.
              </div>
            )}

            {/* Quick navigation buttons */}
            <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
              <button onClick={() => goToTab('adminUsers')}
                style={{padding:'10px 20px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:13}}>
                👥 Manage Users
              </button>
              {features.some(f => f.includes('stablecoin')) && (
                <button onClick={() => goToTab('stablecoinManagement')}
                  style={{padding:'10px 20px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:13}}>
                  💎 Stablecoin Config
                </button>
              )}
              {features.some(f => f.includes('trading')) && (
                <button onClick={() => goToTab('tradingBots')}
                  style={{padding:'10px 20px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:13}}>
                  🤖 Trading Bots
                </button>
              )}
              {features.some(f => f.includes('asset') || f.includes('product')) && (
                <button onClick={() => goToTab('assetTokens')}
                  style={{padding:'10px 20px',background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:13}}>
                  🪙 Asset Tokens
                </button>
              )}
              <button className="admin-btn-primary"
                onClick={() => { setStep(1); setCreatedAdmin(null); setCreatedRetail(null); setError(null); setAdminForm(p=>({...p,username:'',email:'',firstName:'',lastName:'',password:generatePassword(),confirmPassword:''})); setRetailForm(p=>({...p,username:'',email:'',firstName:'',lastName:'',phone:'',password:generatePassword()})); }}
                style={{padding:'10px 20px',fontSize:13}}>
                🔄 Start New Onboarding
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOnboarding;
